// ============================================================
// SettingsTab.jsx  —  Noble Enterprises (tape module)
//
// One section: Jumbo.
//
// FORM ORDER
//   Type → Category → Quality → Width → Meters → Core length
//
// Category CASCADES off Type: it stays disabled until a type is
// picked, then only shows categories belonging to that type.
// Changing the type clears the category, because the old pick
// may not exist under the new type.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. ComboField — dropdown that can switch to "type a new name"
//   C. JumboForm
//   D. SettingsTab — table + form wrapper
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
};

// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%',
  padding: '10px 13px',
  border: `1px solid ${C.border}`,
  borderRadius: '8px',
  fontSize: '14px',
  color: C.text,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, children, ...props }) => (
  <button
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const StatusPill = ({ active }) => (
  <span style={{
    fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '11px',
    background: active ? C.successBg : C.borderSoft,
    color:      active ? C.success   : C.textMuted,
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', gap: '9px',
      background: 'none', border: 'none', cursor: 'pointer',
      padding: 0, fontFamily: 'inherit',
    }}
  >
    <span style={{
      width: '38px', height: '22px', borderRadius: '11px',
      background: checked ? C.brand : C.border,
      position: 'relative', transition: 'background 0.15s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: '3px', left: checked ? '19px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#ffffff', transition: 'left 0.15s',
      }} />
    </span>
    <span style={{ fontSize: '14px', color: C.text }}>{label}</span>
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 22px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 22px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. ComboField
//
// A dropdown that can flip into a text box so the operator can
// create an option that doesn't exist yet.
//
//   disabled  greys the whole thing out (used before a type is picked)
//   hint      small grey text under the field
// ============================================================

const ComboField = ({
  label, options, value, newName, isNew,
  onChange, placeholder, disabled, hint,
}) => (
  <Field label={label}>
    <div style={{ display: 'flex', gap: '8px', opacity: disabled ? 0.5 : 1 }}>
      {isNew ? (
        <TextInput
          autoFocus
          disabled={disabled}
          value={newName}
          onChange={e => onChange({ value: '', newName: e.target.value, isNew: true })}
          placeholder={placeholder}
        />
      ) : (
        <select
          style={inputStyle}
          disabled={disabled}
          value={value}
          onChange={e => onChange({ value: e.target.value, newName: '', isNew: false })}
        >
          <option value="">Select…</option>
          {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange({ value: '', newName: '', isNew: !isNew })}
        style={{
          flexShrink: 0, padding: '0 13px', borderRadius: '8px',
          border: `1px solid ${isNew ? C.brand : C.border}`,
          background: isNew ? C.brand : '#ffffff',
          color: isNew ? '#ffffff' : C.textSoft,
          fontSize: '13px', fontWeight: '600',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}
      >
        {isNew ? 'Cancel' : '+ New'}
      </button>
    </div>
    {hint && (
      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>
    )}
  </Field>
);


// ============================================================
// C. JUMBO FORM
// ============================================================

const emptyForm = {
  id: null,
  name: '',
  tape_type_id: '',
  category_id: '', newCategory: '', isNewCategory: false,
  quality_id: '',  newQuality: '',  isNewQuality: false,
  width_inches: '',
  default_meters: '',
  core_length_meters: '',
  is_active: true,
};

const JumboForm = ({
  form, setForm, tapeTypes, categories, qualities,
  onTypeChange, onSave, onCancel, saving, loadingCategories,
}) => {
  const meters = Number(form.default_meters);
  const core   = Number(form.core_length_meters);
  const expectedCores = (meters > 0 && core > 0) ? Math.floor(meters / core) : null;

  const noTypePicked = !form.tape_type_id;

  return (
    <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        {/* 1. TYPE — drives everything below it */}
        <Field label="Jumbo type">
          <select
            style={inputStyle}
            value={form.tape_type_id}
            onChange={e => onTypeChange(e.target.value)}
          >
            <option value="">Select…</option>
            {tapeTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>

        {/* 2. CATEGORY — filtered by the type above */}
        <ComboField
          label="Category"
          options={categories}
          value={form.category_id}
          newName={form.newCategory}
          isNew={form.isNewCategory}
          disabled={noTypePicked}
          placeholder="Type a new category"
          hint={
            noTypePicked
              ? 'Pick a jumbo type first'
              : (loadingCategories ? 'Loading…' : undefined)
          }
          onChange={({ value, newName, isNew }) => setForm({
            ...form, category_id: value, newCategory: newName, isNewCategory: isNew,
          })}
        />

        {/* 3. QUALITY */}
        <ComboField
          label="Quality"
          options={qualities}
          value={form.quality_id}
          newName={form.newQuality}
          isNew={form.isNewQuality}
          placeholder="Type a new quality"
          onChange={({ value, newName, isNew }) => setForm({
            ...form, quality_id: value, newQuality: newName, isNewQuality: isNew,
          })}
        />

        <Field label="Width (inches)">
          <TextInput
            type="number" step="0.01"
            value={form.width_inches}
            onChange={e => setForm({ ...form, width_inches: e.target.value })}
            placeholder="48"
          />
        </Field>

        <Field label="Total meters">
          <TextInput
            type="number" step="0.01"
            value={form.default_meters}
            onChange={e => setForm({ ...form, default_meters: e.target.value })}
            placeholder="4000"
          />
        </Field>

        <Field label="Core length (meters)">
          <TextInput
            type="number" step="0.01"
            value={form.core_length_meters}
            onChange={e => setForm({ ...form, core_length_meters: e.target.value })}
            placeholder="68"
          />
        </Field>

        {form.id && (
          <Field label="Status">
            <div style={{ paddingTop: '9px' }}>
              <Toggle
                checked={form.is_active}
                onChange={v => setForm({ ...form, is_active: v })}
                label={form.is_active ? 'Active' : 'Inactive'}
              />
            </div>
          </Field>
        )}
      </div>

      {/* Live calculation — catches a typo before saving */}
      {expectedCores !== null && (
        <div style={{
          background: '#ffffff', border: `1px solid ${C.border}`,
          borderRadius: '8px', padding: '13px 18px', marginBottom: '18px',
          display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: C.textSoft }}>
            Expected cores
          </span>
          <span style={{ fontSize: '24px', fontWeight: '700', color: C.text }}>
            {expectedCores}
          </span>
          <span style={{ fontSize: '13px', color: C.textMuted }}>
            {meters.toLocaleString()} m ÷ {core} m per core
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Save jumbo'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// D. MAIN COMPONENT
// ============================================================

const SettingsTab = () => {
  const [rows, setRows]             = useState([]);
  const [tapeTypes, setTapeTypes]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [qualities, setQualities]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingCats, setLoadingCats] = useState(false);
  const [form, setForm]             = useState(null);
  const [saving, setSaving]         = useState(false);
  const [banner, setBanner]         = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  // Load the jumbo list, tape types, and qualities.
  // Categories are NOT loaded here — they depend on the type
  // chosen in the form, so they load on demand.
  const load = useCallback(async () => {
    try {
      const [j, t, q] = await Promise.all([
        axios.get(`${API}/tape/settings/jumbos`,     { headers }),
        axios.get(`${API}/tape/settings/tape-types`, { headers }),
        axios.get(`${API}/tape/settings/qualities`,  { headers }),
      ]);
      setRows(j.data.data || []);
      setTapeTypes(t.data.data || []);
      setQualities((q.data.data || []).filter(x => x.is_active));
    } catch {
      notify('error', 'Could not load jumbo list. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Fetch only the categories belonging to one tape type
  const loadCategoriesForType = async (tapeTypeId) => {
    if (!tapeTypeId) { setCategories([]); return; }
    setLoadingCats(true);
    try {
      const res = await axios.get(
        `${API}/tape/settings/categories?tape_type_id=${tapeTypeId}`,
        { headers }
      );
      setCategories((res.data.data || []).filter(x => x.is_active));
    } catch {
      setCategories([]);
    } finally {
      setLoadingCats(false);
    }
  };

  // Changing the type wipes the category — the previous pick
  // belongs to a different type and would be invalid.
  const handleTypeChange = (tapeTypeId) => {
    setForm(f => ({
      ...f,
      tape_type_id: tapeTypeId,
      category_id: '', newCategory: '', isNewCategory: false,
    }));
    loadCategoriesForType(tapeTypeId);
  };

  const openAdd = () => {
    setForm(emptyForm);
    setCategories([]);
  };

  const openEdit = (row) => {
    setForm({
      ...emptyForm,
      id: row.id,
      name: row.name,
      tape_type_id: String(row.tape_type_id),
      category_id: String(row.category_id),
      quality_id: String(row.quality_id),
      width_inches: row.width_inches,
      default_meters: row.default_meters,
      core_length_meters: row.core_length_meters,
      is_active: row.is_active,
    });
    loadCategoriesForType(row.tape_type_id);
  };

  // ----------------------------------------------------------
  // Save. New category or quality gets created first so we
  // have real ids, then the jumbo itself is saved.
  // ----------------------------------------------------------
  const save = async () => {
    const f = form;

    
    if (!f.tape_type_id) return notify('error', 'Pick a jumbo type');
    if (f.isNewCategory ? !f.newCategory.trim() : !f.category_id)
      return notify('error', 'Pick or add a category');
    if (f.isNewQuality ? !f.newQuality.trim() : !f.quality_id)
      return notify('error', 'Pick or add a quality');
    if (!(Number(f.width_inches) > 0))       return notify('error', 'Width must be more than 0');
    if (!(Number(f.default_meters) > 0))     return notify('error', 'Total meters must be more than 0');
    if (!(Number(f.core_length_meters) > 0)) return notify('error', 'Core length must be more than 0');
    if (Number(f.core_length_meters) > Number(f.default_meters))
      return notify('error', 'Core length cannot be longer than the jumbo');

    setSaving(true);
    try {
      // New category is created under the currently selected type
      let categoryId = f.category_id;
      if (f.isNewCategory) {
        const res = await axios.post(`${API}/tape/settings/categories`, {
          name: f.newCategory.trim(),
          tape_type_id: f.tape_type_id,
        }, { headers });
        categoryId = res.data.data.id;
      }

      let qualityId = f.quality_id;
      if (f.isNewQuality) {
        const res = await axios.post(`${API}/tape/settings/qualities`, {
          name: f.newQuality.trim(),
        }, { headers });
        qualityId = res.data.data.id;
      }

      const payload = {
        
        tape_type_id: f.tape_type_id,
        category_id: categoryId,
        quality_id: qualityId,
        width_inches: f.width_inches,
        default_meters: f.default_meters,
        core_length_meters: f.core_length_meters,
        is_active: f.is_active,
      };

      if (f.id) {
        await axios.put(`${API}/tape/settings/jumbos/${f.id}`, payload, { headers });
        notify('success', 'Jumbo updated');
      } else {
        await axios.post(`${API}/tape/settings/jumbos`, payload, { headers });
        notify('success', 'Jumbo added');
      }

      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/tape/settings/jumbos/${row.id}`, { headers });
      notify('success', 'Jumbo deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Jumbo
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Define each jumbo here. Inward will pick from this list and fill the specs automatically.
            </p>
          </div>
          {!form && (
            <Btn onClick={openAdd}><Plus size={16} /> Add jumbo</Btn>
          )}
        </div>

        {form && (
          <JumboForm
            form={form}
            setForm={setForm}
            tapeTypes={tapeTypes}
            categories={categories}
            qualities={qualities}
            onTypeChange={handleTypeChange}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
            loadingCategories={loadingCats}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Jumbo</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Quality</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Width</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Meters</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Core</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Expected cores</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rows.map(row => {
                const cores = Number(row.core_length_meters) > 0
                  ? Math.floor(Number(row.default_meters) / Number(row.core_length_meters))
                  : 0;
                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{row.name}</td>
                    <td style={tdStyle}>{row.tape_type_name}</td>
                    <td style={tdStyle}>{row.category_name}</td>
                    <td style={tdStyle}>{row.quality_name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{row.width_inches}"</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {Number(row.default_meters).toLocaleString()} m
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{row.core_length_meters} m</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{cores}</td>
                    <td style={tdStyle}><StatusPill active={row.is_active} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn onClick={() => openEdit(row)} title="Edit"><Pencil size={15} /></IconBtn>
                        <IconBtn danger onClick={() => remove(row)} title="Delete"><Trash2 size={15} /></IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No jumbos defined yet. Click "Add jumbo" to create the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;